<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  canManageImports,
  ImportJobList,
  ImportUploadForm,
  useImportAccounts,
  useImportJobs,
} from '@/features/imports'
import { useSession } from '@/app/session'

const { t, locale } = useI18n()
const session = useSession()
const router = useRouter()

const canView = computed(() => canManageImports(session.can))
const ledgerId = () => session.activeLedger.value?.id

const jobs = useImportJobs({ ledgerId, enabled: () => canView.value })
const accounts = useImportAccounts({
  ledgerId: () => ledgerId() ?? '',
  locale: () => locale.value as 'en' | 'hu',
  enabled: () => canView.value,
})

async function onCreate(input: Parameters<typeof jobs.create>[0]): Promise<void> {
  const created = await jobs.create(input).catch(() => undefined)
  if (created !== undefined) await router.push({ name: 'import-job-detail', params: { jobId: created.id } })
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('imports.title')">
      <template #subtitle>{{ t('imports.description') }}</template>
    </PageHeader>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('imports.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('imports.noAccess') }}
    </p>

    <template v-else>
      <ImportUploadForm
        :statement-options="accounts.statementOptions.value"
        :is-creating="jobs.isCreating.value"
        :error-detail="jobs.createErrorDetail.value"
        @submit="onCreate"
      />

      <ImportJobList
        :jobs="jobs.jobs.value"
        :is-loading="jobs.isLoading.value"
        :error="jobs.error.value"
        :retrying="jobs.retrying.value"
        @retry="jobs.retry()"
      />
    </template>
  </PageShell>
</template>
